// API service for Legacy Property Council backend
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
    this.token = localStorage.getItem('access_token');
  }

  // Helper method for making requests
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // Add authorization header if token exists
    if (this.token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${this.token}`;
    }

    try {
      const response = await fetch(url, config);
      
      // Handle different response types
      if (response.status === 204) {
        return { success: true };
      }

      const data = await response.json();
      
      if (!response.ok) {
        // Handle different error response formats
        let errorMessage = `HTTP error! status: ${response.status}`;
        
        if (data && data.detail) {
          if (typeof data.detail === 'string') {
            errorMessage = data.detail;
          } else if (Array.isArray(data.detail)) {
            errorMessage = data.detail.map(err => err.msg || err.message || err).join(', ');
          } else {
            errorMessage = 'Server error occurred';
          }
        } else if (data && data.message) {
          errorMessage = data.message;
        }
        
        throw new Error(errorMessage);
      }

      return data;
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // Membership Application API
  async submitMembershipApplication(applicationData) {
    return this.request('/api/membership/apply', {
      method: 'POST',
      body: JSON.stringify(applicationData),
    });
  }

  async getApplicationStatus(applicationId) {
    return this.request(`/api/membership/application/${applicationId}`);
  }

  // Authentication API
  async login(username, password) {
    const response = await this.request('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });

    if (response.access_token) {
      this.token = response.access_token;
      localStorage.setItem('access_token', response.access_token);
      localStorage.setItem('user_data', JSON.stringify(response.user));
    }

    return response;
  }

  async getCurrentUser() {
    return this.request('/api/auth/me');
  }

  async verifyToken() {
    return this.request('/api/auth/verify-token', {
      method: 'POST',
    });
  }

  logout() {
    this.token = null;
    localStorage.removeItem('access_token');
    localStorage.removeItem('user_data');
  }

  // Portal API (requires authentication)
  async getDashboardStats() {
    return this.request('/api/portal/dashboard');
  }

  async getProperties() {
    return this.request('/api/portal/properties');
  }

  async getPaymentHistory() {
    return this.request('/api/portal/payments');
  }

  async getMaintenanceRequests() {
    return this.request('/api/portal/maintenance');
  }

  // Helper methods
  isAuthenticated() {
    return !!this.token;
  }

  getUserData() {
    const userData = localStorage.getItem('user_data');
    return userData ? JSON.parse(userData) : null;
  }

  setToken(token) {
    this.token = token;
    localStorage.setItem('access_token', token);
  }
}

// Create and export a singleton instance
const apiService = new ApiService();
export default apiService;

// Named exports for specific functions
export const {
  submitMembershipApplication,
  getApplicationStatus,
  login,
  getCurrentUser,
  verifyToken,
  logout,
  getDashboardStats,
  getProperties,
  getPaymentHistory,
  getMaintenanceRequests,
  isAuthenticated,
  getUserData,
} = apiService;